import moment from "moment";
import {
  CombinedIncome,
  VerificationSpecificAdditionaData
} from "../../../types/income";
import { HousingAssistanceSlice } from "../HousingAssistanceSlice";
import { Section } from "views/applications-id-report/components/Section";
import {
  getIncomeJobName,
  getIncomeOriginalNetAndGrossDollars,
  incomeTypeMap
} from "../../../utils";
import { has } from "shared/utils";
import { Show } from "shared/components";
import { SummaryUpdated } from "../../../components/SummaryUpdated";
import {
  ApplicationStatus,
  IncomeSourceProvider,
  ReverifyRequest,
  IncomeStatus,
  VerificationStatus
} from "shared/types";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { formatMonths } from "../../../helpers";
import { IncomeSourceStatusBadge } from "../../../components/IncomeSourceStatusBadge";
import { IncomeStats } from "./components/IncomeStats";
import { SelfReportedStats } from "./components/SelfReportedStats";
import { VerificationSettings } from "types/landlord/reports";
import { IncomeHeader } from "./components/IncomeHeader";
import {
  EditIncomeModalData,
  EditTransactionsDrawerData,
  OverrideIncomeModalData
} from "../../../types";
import { ProcessingStatus } from "./components/ProcessingStatus";
import { ReverifyAttempt } from "../../ReverifySlice/types";
import {
  isReverifyTabAPlaceholder,
  getReverifyAttemptsPerIncome
} from "../../ReverifySlice/utils";
import { Applicant } from "types/application";
import { VerifiedIncomeDetails } from "./components/VerifiedIncomeDetails";

export const IncomeSlice = ({
  income,
  applicationStatus,
  applicationId,
  additionalData,
  isManualApplicant,
  setShowEditIncomeModal,
  setShowOverrideVerificationModal,
  setShowEditTransactionsDrawer,
  setShowReverifyIncomeModal,
  setHideProjectedDebtSection,
  verificationId,
  applicantId,
  pendingReverifyRequest,
  completedReverifyRequest,
  failedReverifyRequest,
  verificationStatus,
  onReverifyRequestCancelled,
  applicant,
  incomeSourceProviders,
  incomeSourceTrustScores
}: {
  income: CombinedIncome;
  applicationStatus: ApplicationStatus;
  applicationId?: string | number;
  additionalData?: VerificationSpecificAdditionaData | null;
  settings?: VerificationSettings;
  isManualApplicant: boolean;
  setShowEditIncomeModal: (value: false | EditIncomeModalData) => void;
  setShowOverrideVerificationModal: (
    value: false | OverrideIncomeModalData
  ) => void;
  setShowEditTransactionsDrawer: (
    value: false | EditTransactionsDrawerData
  ) => void;
  setShowReverifyIncomeModal: Dispatch<SetStateAction<number | false>>;
  setHideProjectedDebtSection: (value: boolean) => void;
  verificationId?: number;
  applicantId?: number;
  pendingReverifyRequest?: ReverifyRequest | null;
  completedReverifyRequest?: ReverifyRequest | null;
  failedReverifyRequest?: ReverifyRequest | null;
  verificationStatus?: VerificationStatus;
  onReverifyRequestCancelled?: () => void;
  applicant: Applicant;
  incomeSourceProviders?: Record<number, IncomeSourceProvider | string>;
  incomeSourceTrustScores?: Record<number, number>;
}) => {
  const [selectedReverifyAttemptIdx, setSelectedReverifyAttemptIdx] =
    useState(0);
  const diff = moment().diff(moment(income.start_date), "months");

  const reverifyAttempts: ReverifyAttempt[] = useMemo(
    () =>
      getReverifyAttemptsPerIncome({
        income,
        additionalData,
        pendingReverifyRequest,
        completedReverifyRequest,
        failedReverifyRequest,
        isManualApplicant
      }),
    [
      income,
      additionalData,
      pendingReverifyRequest,
      completedReverifyRequest,
      failedReverifyRequest,
      isManualApplicant
    ]
  );
  const selectedReverifyAttempt = reverifyAttempts[selectedReverifyAttemptIdx];

  const { originalNetMonthlyIncomeDollars, originalGrossMonthlyIncomeDollars } =
    getIncomeOriginalNetAndGrossDollars(income, selectedReverifyAttempt);

  const isIncomeIncomplete = [
    IncomeStatus.Processing,
    IncomeStatus.Pending
  ].includes(income.status);

  const hasInProgressReverify = Boolean(
    pendingReverifyRequest ||
    (completedReverifyRequest && income.status === IncomeStatus.Processing)
  );

  if (income.type === "housing_assistance")
    return (
      <HousingAssistanceSlice
        amountCents={income.amount_cents}
        assets={income.assets}
        incomeId={income.id}
        applicant={applicant}
        verificationId={income.verification_id}
      />
    );

  return (
    <Section
      id={`combined-income-section-${income.id}`}
      maxWidth="1000px"
      sectionTitle={
        <p className="flex items-center gap-2 text-content-400 font-medium text-sm">
          {income.additional_data ? (
            <>
              {getIncomeJobName(income.additional_data)}
              <span className="block size-1.5 shrink-0 bg-content-100 rounded-full" />
            </>
          ) : (
            ""
          )}
          {incomeTypeMap[income.type] || income.type}
          <span className="block size-1.5 shrink-0 bg-content-100 rounded-full" />
          <IncomeSourceStatusBadge
            income={income}
            forceInProgress={isReverifyTabAPlaceholder(selectedReverifyAttempt)}
          />
        </p>
      }
      sectionClassName="md:p-6 md:pt-8 pt-3 pl-4 pb-12"
      rightElement={
        <p className="text-content-400 text-sm font-medium">
          {income.type === "offer_letter"
            ? moment(income.start_date).format("ll")
            : `${moment(income.start_date).format("ll")} - Present (${formatMonths(diff)})`}
        </p>
      }
    >
      <IncomeHeader
        setShowEditIncomeModal={setShowEditIncomeModal}
        setShowEditTransactionsDrawer={setShowEditTransactionsDrawer}
        setShowReverifyIncomeModal={setShowReverifyIncomeModal}
        setShowOverrideVerificationModal={setShowOverrideVerificationModal}
        income={income}
        applicationStatus={applicationStatus}
        applicationId={applicationId}
        selectedReverifyAttempt={selectedReverifyAttempt}
        isManualApplicant={isManualApplicant}
        allBankStatements={
          additionalData?.bank_statement?.allBankStatements || {}
        }
        isIncomeIncomplete={isIncomeIncomplete}
        hasInProgressReverify={hasInProgressReverify}
        hasFailedReverify={Boolean(failedReverifyRequest)}
      />
      <IncomeStats
        income={income}
        selectedReverifyAttempt={selectedReverifyAttempt}
        forcePlaceholdersDisplay={isReverifyTabAPlaceholder(
          selectedReverifyAttempt
        )}
      />
      <Show
        if={
          !isReverifyTabAPlaceholder(selectedReverifyAttempt) &&
          (has(income.additional_data?.overrides?.gross_monthly_income) ||
            has(income.additional_data?.overrides?.net_monthly_income))
        }
      >
        <SummaryUpdated
          className="mb-10"
          changes={income.additional_data?.overrides}
          original={{
            gross: originalNetMonthlyIncomeDollars,
            net: originalGrossMonthlyIncomeDollars
          }}
        />
      </Show>

      <SelfReportedStats income={income} />
      {income.status === "processing" &&
      !(pendingReverifyRequest || completedReverifyRequest) ? (
        <ProcessingStatus income={income} />
      ) : (
        <VerifiedIncomeDetails
          reverifyAttempts={reverifyAttempts}
          income={income}
          selectedReverifyAttemptIdx={selectedReverifyAttemptIdx}
          setSelectedReverifyAttemptIdx={setSelectedReverifyAttemptIdx}
          selectedReverifyAttempt={selectedReverifyAttempt}
          setHideProjectedDebtSection={setHideProjectedDebtSection}
          additionalData={additionalData}
          pendingReverifyRequest={pendingReverifyRequest}
          applicationId={applicationId}
          verificationId={verificationId}
          applicantId={applicantId}
          verificationStatus={verificationStatus}
          onReverifyRequestCancelled={onReverifyRequestCancelled}
          incomeSourceProviders={incomeSourceProviders}
          incomeSourceTrustScores={incomeSourceTrustScores}
        />
      )}
    </Section>
  );
};
